import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, interval, merge, of, switchMap, timer } from 'rxjs';
import { DeliveryDateTableComponent } from '../../shared/components/delivery-date-table/delivery-date-table.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { WERK_LABELS } from '../../shared/constants/order-document-labels';
import { ILieferterminOverviewRow, WerkType } from '../../shared/models/order-document.model';
import { OrderDocumentService } from '../../shared/services/order-document.service';
import { SocketService } from '../../shared/services/socket.service';

/**
 * Read-only production TV board of today's Liefertermine. Reached by URL only
 * (`/delivery-dates-overview`, not in the navigation menu) and rendered chrome-free (see
 * app.component's `hideDefaultLayout`). Data comes from the public GET /api/liefertermine-overview endpoint,
 * already filtered to today and sorted sicher-first.
 *
 * Each TV is set up once with a URL for its location: `?factory=1,2` limits the board to Werk 1 and 2
 * (unknown numbers are ignored; no/empty param shows all Werke).
 */
@Component({
  selector: 'app-delivery-dates-overview',
  standalone: true,
  imports: [DeliveryDateTableComponent, PaginationComponent],
  templateUrl: './delivery-dates-overview.component.html',
  styleUrls: ['./delivery-dates-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryDatesOverviewComponent implements OnInit {
  private readonly orderDocumentService = inject(OrderDocumentService);
  private readonly socketService = inject(SocketService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);

  /**
   * Fallback poll interval. WebSocket pushes (liefertermine:changed) are the primary update path
   * and refresh the board instantly; this long poll only guarantees eventual consistency if the
   * socket connection is ever lost.
   */
  private static readonly REFRESH_INTERVAL_MS = 3_600_000;
  /** How many rows are shown per screen on the TV board. */
  private static readonly PAGE_SIZE = 10;
  /** How often the board auto-advances to the next page. */
  private static readonly PAGE_INTERVAL_MS = 10_000;

  /** Exposed for the footer's `app-pagination` binding (template can't read a static field). */
  readonly pageSize = DeliveryDatesOverviewComponent.PAGE_SIZE;

  /**
   * Werke this screen shows, read once from `?factory=1,2` (the URL is fixed per TV, so no need to
   * react to later changes). Numbers map to WerkType (`1` → WERK_1); invalid entries are dropped.
   */
  readonly werke: WerkType[] = (this.route.snapshot.queryParamMap.get('factory') ?? '')
    .split(',')
    .map(n => `WERK_${n.trim()}`)
    .filter((w): w is WerkType => (Object.values(WerkType) as string[]).includes(w));

  /** Header hint so whoever sets up a screen can see the filter took effect, e.g. "Werk 1 · Werk 2". */
  readonly werkLabel = this.werke.map(w => WERK_LABELS[w]).join(' · ');

  readonly rows = signal<ILieferterminOverviewRow[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);
  readonly now = signal(new Date());
  readonly currentPage = signal(0);

  readonly currentTime = computed(() => this.now().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }));
  readonly currentDate = computed(() =>
    this.now().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })
  );

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.rows().length / DeliveryDatesOverviewComponent.PAGE_SIZE)));

  /**
   * The 0-based page actually shown, clamped against `totalPages()` here rather than in an
   * `effect()` that both reads and writes `currentPage` (an anti-pattern), so the display
   * self-corrects if `rows()` shrinks on a refresh without any imperative resetting.
   */
  readonly displayPage = computed(() => Math.min(this.currentPage(), this.totalPages() - 1));

  readonly pagedRows = computed(() => {
    const start = this.displayPage() * DeliveryDatesOverviewComponent.PAGE_SIZE;
    return this.rows().slice(start, start + DeliveryDatesOverviewComponent.PAGE_SIZE);
  });

  ngOnInit(): void {
    // Idempotent connect; the root singleton keeps one socket alive across the app.
    this.socketService.connect();

    // Fetch immediately, then refresh whenever the socket pushes a liefertermine:changed event or
    // the long fallback timer ticks. catchError inside the inner stream keeps the outer stream
    // alive so a single failed request never stops the always-on board from recovering.
    merge(timer(0, DeliveryDatesOverviewComponent.REFRESH_INTERVAL_MS), this.socketService.liefertermineChanged$)
      .pipe(
        switchMap(() =>
          this.orderDocumentService.getTodaysLiefertermine(this.werke.join(',')).pipe(
            catchError(() => {
              this.error.set(true);
              this.loading.set(false);
              return of(null);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(response => {
        if (response) {
          this.rows.set(response.DATA ?? []);
          this.error.set(false);
        }
        this.loading.set(false);
      });

    // Live clock in the header, ticking every second.
    interval(1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.now.set(new Date()));

    // Auto-advance the page on a loop; no user input is possible on the TV board. Modulo against
    // the live totalPages() on every tick wraps back to the first page and self-corrects even if
    // currentPage was momentarily out of range (e.g. rows() just shrank on a refresh).
    interval(DeliveryDatesOverviewComponent.PAGE_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.currentPage.update(p => (p + 1) % this.totalPages()));
  }
}
